import React, { useEffect, useState } from 'react';
import { trips as tripsApi, extractError } from '../../services/api';
import { TripStatusBadge } from '../ui/StatusBadge';
import { Modal } from '../ui/Modal';
import { useToast } from '../../store/ToastContext';
import { useAuth } from '../../store/AuthContext';

function DetailRow({ label, value }) {
  return (
    <div className="flex gap-3">
      <span className="text-sm text-gray-500 w-36 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-900 font-medium">{value || '—'}</span>
    </div>
  );
}

function TimelineItem({ event }) {
  const eventColors = {
    created:    'bg-gray-400',
    dispatched: 'bg-yellow-500',
    started:    'bg-green-500',
    paused:     'bg-orange-500',
    resumed:    'bg-blue-500',
    completed:  'bg-teal-500',
    cancelled:  'bg-red-500',
    fuel_stop:  'bg-purple-500',
    incident:   'bg-red-600',
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${eventColors[event.event_type] || 'bg-gray-400'}`} />
        <div className="w-px flex-1 bg-gray-200 my-1" />
      </div>
      <div className="pb-4 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-gray-900 capitalize">{event.event_type?.replace(/_/g, ' ')}</span>
          <span className="text-xs text-gray-400">{new Date(event.timestamp).toLocaleString()}</span>
        </div>
        {event.actor_name && <p className="text-xs text-gray-500">by {event.actor_name}</p>}
        {event.notes && <p className="text-xs text-gray-600 mt-1">{event.notes}</p>}
      </div>
    </div>
  );
}

export function TripDetail({ trip, onClose, onAction, onRefresh }) {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [actionLoading, setActLoading] = useState('');

  const canAct = hasRole(['administrator', 'fleet_manager', 'dispatcher']);

  useEffect(() => {
    if (activeTab === 'timeline') {
      setLoadingTimeline(true);
      tripsApi.timeline(trip.id)
        .then(data => setTimeline(Array.isArray(data) ? data : data?.data || []))
        .catch(() => toast.error('Failed to load timeline'))
        .finally(() => setLoadingTimeline(false));
    }
  }, [activeTab, trip.id]);

  const handleAction = async (action, label) => {
    setActLoading(action);
    try {
      const updated = await tripsApi[action](trip.id);
      toast.success(`Trip ${label} successfully`);
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setActLoading('');
    }
  };

  const ACTIONS = [
    { status: 'draft',       action: 'dispatch', label: 'Dispatch',  cls: 'btn-primary' },
    { status: 'dispatched',  action: 'start',    label: 'Start Trip', cls: 'btn-primary' },
    { status: 'in_progress', action: 'pause',    label: 'Pause',     cls: 'bg-orange-500 hover:bg-orange-600 text-white btn-primary' },
    { status: 'in_progress', action: 'complete', label: 'Complete',  cls: 'bg-teal-600 hover:bg-teal-700 text-white btn-primary' },
    { status: 'paused',      action: 'resume',   label: 'Resume',    cls: 'btn-primary' },
  ];

  const relevantActions = ACTIONS.filter(a => a.status === trip.status && canAct);

  return (
    <Modal isOpen size="xl" onClose={onClose} title={
      <div className="flex items-center gap-3">
        <span className="font-bold">{trip.trip_number}</span>
        <TripStatusBadge status={trip.status} />
      </div>
    }>
      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200 -mt-1">
        {['details', 'timeline'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
        <div className="space-y-6">
          {/* Route */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Route</h4>
            <div className="space-y-2">
              <DetailRow label="Origin"               value={trip.origin_address} />
              <DetailRow label="Destination"          value={trip.destination_address} />
              <DetailRow label="Sched. Departure"     value={trip.scheduled_departure && new Date(trip.scheduled_departure).toLocaleString()} />
              <DetailRow label="Actual Departure"     value={trip.actual_departure && new Date(trip.actual_departure).toLocaleString()} />
              <DetailRow label="Sched. Arrival"       value={trip.scheduled_arrival && new Date(trip.scheduled_arrival).toLocaleString()} />
              <DetailRow label="Actual Arrival"       value={trip.actual_arrival && new Date(trip.actual_arrival).toLocaleString()} />
              <DetailRow label="Est. Distance"        value={trip.estimated_distance_miles ? `${parseFloat(trip.estimated_distance_miles).toFixed(1)} miles` : null} />
              <DetailRow label="Actual Distance"      value={trip.actual_distance_miles ? `${parseFloat(trip.actual_distance_miles).toFixed(1)} miles` : null} />
            </div>
          </div>

          <div className="border-t border-gray-100" />

          {/* Assignment */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Assignment</h4>
            <div className="space-y-2">
              <DetailRow label="Driver"   value={trip.driver_name || 'Unassigned'} />
              <DetailRow label="Vehicle"  value={trip.unit_number ? `${trip.unit_number} — ${trip.make || ''} ${trip.model || ''}`.trim() : 'Unassigned'} />
              <DetailRow label="Dispatcher" value={trip.dispatcher_name} />
            </div>
          </div>

          <div className="border-t border-gray-100" />

          {/* Cargo */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Cargo</h4>
            <div className="space-y-2">
              <DetailRow label="Load Weight"    value={trip.load_weight_lbs ? `${parseInt(trip.load_weight_lbs).toLocaleString()} lbs` : null} />
              <DetailRow label="Description"    value={trip.cargo_description} />
              <DetailRow label="HAZMAT"         value={trip.is_hazmat ? `Yes — ${trip.hazmat_class || 'class not specified'}` : 'No'} />
              <DetailRow label="Special Notes"  value={trip.special_instructions} />
            </div>
          </div>

          <div className="border-t border-gray-100" />

          {/* Fuel */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Fuel</h4>
            <div className="space-y-2">
              <DetailRow label="Est. Fuel" value={trip.estimated_fuel_gallons ? `${parseFloat(trip.estimated_fuel_gallons).toFixed(1)} gallons` : null} />
              <DetailRow label="Actual Fuel" value={trip.actual_fuel_gallons ? `${parseFloat(trip.actual_fuel_gallons).toFixed(1)} gallons` : null} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'timeline' && (
        <div>
          {loadingTimeline ? (
            <div className="py-8 flex justify-center">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : timeline.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">No timeline events</div>
          ) : (
            <div>
              {timeline.map(e => <TimelineItem key={e.id} event={e} />)}
            </div>
          )}
        </div>
      )}

      {/* Action footer */}
      {relevantActions.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3 flex-wrap">
          {relevantActions.map(a => (
            <button
              key={a.action}
              onClick={() => handleAction(a.action, a.label)}
              disabled={!!actionLoading}
              className={`${a.cls} disabled:opacity-50`}
            >
              {actionLoading === a.action ? 'Processing…' : a.label}
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
